<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import LayoutLanguageSwitcher from '~/components/layout/LanguageSwitcher.vue'
import LayoutColorModeButton from '~/components/layout/ColorModeButton.vue'

const pageLocale = useState<string>('page-locale', () => 'fa')
const dir = useDir()

const props = withDefaults(
  defineProps<{
    logoText?: string | Record<string, string>
    logoImage?: string
    menuItems?: Array<{
      label?: string | Record<string, string>
      to?: string
    }>
    authCta?: {
      label?: string | Record<string, string>
      to?: string
    }
    builderPreview?: boolean
    /** Forces mobile/desktop nav in builder preview (Tailwind lg: uses viewport, not frame width). */
    previewDevice?: 'desktop' | 'mobile'
    onNavigate?: (to: string) => void
  }>(),
  {
    menuItems: () => [],
    builderPreview: false,
  },
)

const headerOpen = ref(false)

const isRtl = computed(() => dir.value === 'rtl')
/**
 * Logo on the start edge, menu/utilities on the end edge:
 * - RTL → logo left, toggle+panel right
 * - LTR → logo right, toggle+panel left
 * (swapped from the previous mapping)
 */
const menuSide = computed(() => (isRtl.value ? 'right' : 'left'))

/**
 * Builder preview teleports the mobile panel into the frame so it can't cover the
 * builder chrome; the published site uses the real viewport.
 */
/** Logo acts as a real link on the published site, but only re-renders the preview in the builder. */
const logoTag = computed(() =>
  props.builderPreview ? 'button' : resolveComponent('NuxtLink'),
)

const menuTeleportTarget = computed(() =>
  props.builderPreview ? '.preview-viewport' : 'body',
)
const menuOverlayClass = computed(() =>
  props.builderPreview ? 'absolute inset-0 z-[60]' : 'fixed inset-0 z-[60]',
)

/** Builder mobile frame, or real viewport below lg. */
const useCompactNav = computed(() => props.previewDevice === 'mobile')
/** Builder desktop frame — always show horizontal nav. */
const useExpandedNav = computed(() => props.previewDevice === 'desktop')

function resolveVal(value: unknown, loc: string): any {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map((item) => resolveVal(item, loc))
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (rec[loc] !== undefined) return rec[loc]
    return Object.fromEntries(
      Object.entries(rec).map(([k, v]) => [k, resolveVal(v, loc)]),
    )
  }
  return value
}

const resolvedLogoText = computed(() => {
  const t = props.logoText
  if (typeof t === 'string') return t
  return t
    ? String(resolveVal(t, pageLocale.value))
    : (pageLocale.value === 'fa' ? 'سایت' : 'Site')
})

const navItems = computed(() => {
  return (props.menuItems ?? []).map((item) => ({
    label: typeof item.label === 'string'
      ? item.label
      : (resolveVal(item.label, pageLocale.value) as string),
    to: item.to ?? '#',
  }))
})

const { enabledKeys } = useSiteModules()
const isPlatform = useState<boolean>('is-platform', () => true)

/** Patient portal CTA belongs on tenant clinic sites only — not on aradcore.com / platform landing. */
const showPatientAuth = computed(() => {
  if (isPlatform.value && !props.builderPreview) return false
  return enabledKeys.value.has('healthRecord')
    || enabledKeys.value.has('onlineBooking')
    || enabledKeys.value.has('blog')
})
const { data: patientSession } = await useFetch<{ loggedIn: boolean }>('/api/patient/session', {
  default: () => ({ loggedIn: false }),
  immediate: showPatientAuth.value,
})
const patientAuthTo = computed(() => (patientSession.value?.loggedIn ? '/patient' : '/patient/login'))
const patientAuthLabel = computed(() => {
  if (patientSession.value?.loggedIn) {
    return pageLocale.value === 'fa' ? 'حساب من' : 'My account'
  }
  return pageLocale.value === 'fa' ? 'ورود / ثبت‌نام' : 'Sign in / Sign up'
})
/**
 * Desktop-only CTA: hidden below lg (mobile/tablet — same threshold as the nav and
 * MobileTabBar). `lg:` tracks the viewport, not the builder frame, so the builder's
 * mobile device mode is forced off explicitly.
 */
const patientAuthClass = computed(() => {
  if (props.builderPreview && props.previewDevice) {
    return props.previewDevice === 'mobile' ? 'hidden' : ''
  }
  return 'hidden lg:block'
})

const navMenuItems = computed<NavigationMenuItem[]>(() =>
  navItems.value.map((item) => {
    if (props.builderPreview) {
      return {
        label: item.label,
        onSelect: (e: Event) => {
          e.preventDefault()
          handleNavClick(item.to)
        },
      }
    }
    return {
      label: item.label,
      to: item.to,
    }
  }),
)

const authCtaTo = computed(() => props.authCta?.to || '')
const authCtaLabel = computed(() => {
  const label = props.authCta?.label
  if (!label) return ''
  if (typeof label === 'string') return label
  return String(resolveVal(label, pageLocale.value) || '')
})

function onAuthCtaClick() {
  if (!authCtaTo.value) return
  if (props.builderPreview) {
    handleNavClick(authCtaTo.value)
    return
  }
}

function onPatientAuthClick() {
  if (props.builderPreview) {
    handleNavClick(patientAuthTo.value)
  }
}

function handleNavClick(to: string) {
  headerOpen.value = false
  if (props.builderPreview && props.onNavigate) {
    props.onNavigate(to)
  }
}

watch(
  () => props.previewDevice,
  () => {
    headerOpen.value = false
  },
)

const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    headerOpen.value = false
  },
)
</script>

<template>
  <div :dir="dir">
    <!--
      One header for builder preview and published site so both render identically.
      dir=ltr on the bar so order-* maps to physical left/right (logo ↔ actions swap by locale).
    -->
    <header
      class="sticky top-0 z-40 border-b border-default bg-default/75 backdrop-blur"
      style="height: var(--ui-header-height, 4rem)"
    >
      <div
        dir="ltr"
        class="mx-auto flex h-full w-full min-w-0 items-center justify-between gap-2 px-3 sm:gap-3 sm:px-6 lg:px-8"
      >
        <component
          :is="logoTag"
          :type="builderPreview ? 'button' : undefined"
          :to="builderPreview ? undefined : '/'"
          class="flex min-w-0 shrink items-center gap-2"
          :class="isRtl ? 'order-1' : 'order-3'"
          @click="handleNavClick('/')"
        >
          <UAvatar
            v-if="logoImage"
            alt="logo"
            :src="logoImage"
          />
          <span class="truncate text-xl">{{ resolvedLogoText }}</span>
        </component>

        <!--
          v-if (not class hidden): UNavigationMenu / UButton ship display utilities
          that can override Tailwind `hidden` depending on CSS order.
        -->
        <UNavigationMenu
          v-if="navMenuItems.length && !useCompactNav"
          variant="link"
          :dir="dir"
          :items="navMenuItems"
          class="order-2 min-w-0 flex-1 justify-center overflow-x-auto"
          :class="useExpandedNav ? 'flex' : 'hidden lg:flex'"
        />

        <div
          class="flex h-8 shrink-0 items-center gap-1"
          :class="isRtl ? 'order-3' : 'order-1'"
        >
          <div class="header-actions flex h-8 shrink-0 items-center gap-0.5">
            <LayoutLanguageSwitcher />
            <LayoutColorModeButton />
            <!-- Wrapper div: UButton ships display utilities that can override Tailwind `hidden`. -->
            <div
              v-if="showPatientAuth"
              :class="patientAuthClass"
              class="shrink-0"
            >
              <UButton
                :to="builderPreview ? undefined : patientAuthTo"
                color="primary"
                variant="soft"
                size="sm"
                class="header-actions-cta ms-0.5"
                @click="onPatientAuthClick"
              >
                {{ patientAuthLabel }}
              </UButton>
            </div>
          </div>

          <UButton
            v-if="authCtaTo"
            :to="builderPreview ? undefined : authCtaTo"
            color="primary"
            size="sm"
            class="ms-1"
            @click="onAuthCtaClick"
          >
            {{ authCtaLabel }}
          </UButton>

          <UButton
            v-if="navItems.length && !useExpandedNav"
            :icon="headerOpen ? 'i-lucide-x' : 'i-lucide-menu'"
            color="neutral"
            variant="ghost"
            size="sm"
            square
            class="rounded-full"
            :aria-label="headerOpen ? 'بستن منو' : 'باز کردن منو'"
            :class="useCompactNav ? undefined : 'lg:hidden'"
            @click="headerOpen = !headerOpen"
          />
        </div>
      </div>

      <!--
        In the builder the panel is teleported into .preview-viewport so it stays inside the
        phone frame (not the builder chrome); on the published site it covers the viewport.
      -->
      <Teleport
        v-if="navItems.length && (useCompactNav || !useExpandedNav)"
        :to="menuTeleportTarget"
        defer
      >
        <div
          v-show="headerOpen"
          :class="[menuOverlayClass, useCompactNav ? '' : 'lg:hidden']"
        >
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <div
              v-if="headerOpen"
              class="absolute inset-0 bg-elevated/75"
              @click="headerOpen = false"
            />
          </Transition>

          <Transition
            enter-active-class="transition duration-200 ease-in-out"
            :enter-from-class="menuSide === 'right' ? 'translate-x-full' : '-translate-x-full'"
            enter-to-class="translate-x-0"
            leave-active-class="transition duration-200 ease-in-out"
            leave-from-class="translate-x-0"
            :leave-to-class="menuSide === 'right' ? 'translate-x-full' : '-translate-x-full'"
          >
            <div
              v-if="headerOpen"
              class="absolute inset-y-0 flex w-[min(100%,20rem)] flex-col divide-y divide-default bg-default shadow-lg ring ring-default"
              :class="menuSide === 'right' ? 'right-0' : 'left-0'"
              :dir="dir"
            >
              <div
                dir="ltr"
                class="flex h-(--ui-header-height,4rem) shrink-0 items-center justify-between gap-3 px-4"
              >
                <button
                  type="button"
                  class="flex min-w-0 items-center gap-2"
                  :class="isRtl ? 'order-1' : 'order-2'"
                  @click="handleNavClick('/')"
                >
                  <UAvatar
                    v-if="logoImage"
                    alt="logo"
                    :src="logoImage"
                  />
                  <span class="truncate text-xl">{{ resolvedLogoText }}</span>
                </button>

                <div
                  class="header-actions flex h-8 shrink-0 items-center gap-0.5"
                  :class="isRtl ? 'order-2' : 'order-1'"
                >
                  <LayoutLanguageSwitcher />
                  <LayoutColorModeButton />
                  <UButton
                    icon="i-lucide-x"
                    color="neutral"
                    variant="ghost"
                    size="sm"
                    square
                    class="rounded-full"
                    aria-label="بستن منو"
                    @click="headerOpen = false"
                  />
                </div>
              </div>

              <div class="flex-1 overflow-y-auto p-4">
                <UNavigationMenu
                  :dir="dir"
                  :items="navMenuItems"
                  orientation="vertical"
                  class="-mx-2.5"
                />
                <UButton
                  v-if="authCtaTo"
                  color="primary"
                  block
                  class="mt-4"
                  @click="onAuthCtaClick"
                >
                  {{ authCtaLabel }}
                </UButton>
              </div>
            </div>
          </Transition>
        </div>
      </Teleport>
    </header>
  </div>
</template>

<style scoped>
/* Keep header utility icons on one optical baseline (Popover/ClientOnly wrappers differ). */
:deep(.header-actions) {
  line-height: 0;
}

:deep(.header-actions :is(button, a)[data-slot='base']:not(.header-actions-cta)) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  line-height: 0;
}

:deep(.header-actions :is(button, a)[data-slot='base']:not(.header-actions-cta) :is(svg, .iconify)) {
  display: block;
  width: 1rem;
  height: 1rem;
  margin: 0;
  flex-shrink: 0;
}

:deep(.header-actions .header-actions-cta[data-slot='base']) {
  height: 2rem;
  width: auto;
  padding-inline: 0.625rem;
  line-height: 1.25;
  white-space: nowrap;
}
</style>
